import { ensureFirebaseAuth, uploadEncryptedFile, downloadEncryptedFile, getFirebaseUid } from './firebaseStorage';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app';
import type { HorusSigner } from '../signers/types';

const DB_KEY_PREFIX = 'horus-xmtp-dbkey-';
const BACKUP_KEY_PREFIX = 'horus-xmtp-bkkey-';
const BACKUP_VERSION = 1;

// ---- Backup encryption key derivation ----

/**
 * Derive a deterministic 32-byte AES-GCM key from a wallet signature.
 * The same wallet will always produce the same key, making backups
 * portable across devices without storing the key separately.
 *
 * The derived key is cached in localStorage so that subsequent backups
 * (including periodic ones) don't require repeated wallet signatures
 * (which would trigger a popup for non-custodial users every time).
 */
async function deriveBackupKey(signer: HorusSigner): Promise<Uint8Array> {
  const address = signer.getIdentifier().identifier.toLowerCase();
  const cacheKey = BACKUP_KEY_PREFIX + address;

  // Check localStorage cache first — avoids wallet popup on every backup
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const arr = JSON.parse(cached);
      if (Array.isArray(arr) && arr.length === 32) {
        return new Uint8Array(arr);
      }
    } catch { /* corrupted, re-derive */ }
  }

  const message = `horus-xmtp-backup-v1-${address}`;
  const signature = await signer.signMessage(message);
  const hashBuf = await crypto.subtle.digest('SHA-256', signature);
  const key = new Uint8Array(hashBuf);

  // Cache for future backups
  localStorage.setItem(cacheKey, JSON.stringify(Array.from(key)));
  return key;
}

// ---- AES-GCM encrypt / decrypt ----

async function encryptBlob(data: Uint8Array, key: Uint8Array): Promise<{ payload: Uint8Array; nonce: Uint8Array }> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const cryptoKey = await crypto.subtle.importKey('raw', key, 'AES-GCM', false, ['encrypt']);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, cryptoKey, data);
  return { payload: new Uint8Array(encrypted), nonce };
}

async function decryptBlob(payload: Uint8Array, key: Uint8Array, nonce: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey('raw', key, 'AES-GCM', false, ['decrypt']);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, cryptoKey, payload);
  return new Uint8Array(decrypted);
}

// ---- OPFS file operations ----

/**
 * Recursively read all files from an OPFS directory handle.
 * Builds a flat map using path-like keys (e.g. "subdir/file.db3").
 */
async function readOpfsDir(
  dirHandle: FileSystemDirectoryHandle,
  prefix: string,
): Promise<Map<string, Uint8Array>> {
  const files = new Map<string, Uint8Array>();
  for await (const [name, handle] of (dirHandle as any).entries()) {
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'file') {
      try {
        const file = await handle.getFile();
        const buf = await file.arrayBuffer();
        files.set(path, new Uint8Array(buf));
      } catch (err) {
        console.warn(`[XMTP Backup] Cannot read OPFS file "${path}":`, err);
      }
    } else if (handle.kind === 'directory') {
      const subFiles = await readOpfsDir(handle, path);
      subFiles.forEach((v, k) => files.set(k, v));
    }
  }
  return files;
}

/**
 * Export OPFS files for backup.
 * Strategy:
 *   1. Try matching files by wallet address (with and without 0x prefix)
 *   2. If no address-specific files found, export ALL OPFS files
 *      (OPFS is per-origin so all files belong to our app)
 */
async function exportOpfsFiles(address: string): Promise<Map<string, Uint8Array>> {
  try {
    const root = await navigator.storage?.getDirectory?.();
    if (!root) {
      console.warn('[XMTP Backup] OPFS root directory not available');
      return new Map();
    }

    // Read all files recursively
    const allFiles = await readOpfsDir(root, '');
    console.log(`[XMTP Backup] OPFS total files: ${allFiles.size}`, Array.from(allFiles.keys()));

    if (allFiles.size === 0) return allFiles;

    // Try to filter by address (with and without 0x prefix)
    const addr = address.toLowerCase();
    const addrNoPrefix = addr.startsWith('0x') ? addr.slice(2) : addr;

    const matched = new Map<string, Uint8Array>();
    allFiles.forEach((bytes, name) => {
      const nameLower = name.toLowerCase();
      if (nameLower.includes(addr) || nameLower.includes(addrNoPrefix)) {
        matched.set(name, bytes);
      }
    });

    if (matched.size > 0) {
      console.log(`[XMTP Backup] Matched ${matched.size} files for address ${addr}`);
      return matched;
    }

    // No address-specific files — XMTP may use inbox ID or hash for file names.
    // Export all files since OPFS is per-origin (all files belong to our app).
    console.log(`[XMTP Backup] No address match — exporting all ${allFiles.size} OPFS files`);
    return allFiles;
  } catch (err) {
    console.warn('[XMTP Backup] OPFS enumeration failed:', err);
    return new Map();
  }
}

/**
 * Import OPFS files from a backup. Writes files to OPFS before the XMTP client
 * is created so there's no lock contention.
 */
async function importOpfsFiles(files: Map<string, Uint8Array>): Promise<void> {
  const root = await navigator.storage?.getDirectory?.();
  if (!root) throw new Error('OPFS not available');

  const entries = Array.from(files.entries());
  for (let i = 0; i < entries.length; i++) {
    const [name, bytes] = entries[i];

    // Handle subdirectory paths (e.g. "subdir/file.db3") by creating
    // intermediate directories — OPFS doesn't support slashes in names.
    const parts = name.split('/');
    let dir: FileSystemDirectoryHandle = root;
    for (let j = 0; j < parts.length - 1; j++) {
      dir = await dir.getDirectoryHandle(parts[j], { create: true });
    }
    const fileName = parts[parts.length - 1];

    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await (fileHandle as any).createWritable();
    await writable.write(bytes);
    await writable.close();
  }
}

// ---- Packaging: combine OPFS files + dbKey into a single blob ----

interface BackupHeader {
  version: number;
  dbKey: number[];  // 32-byte encryption key as array
  files: { name: string; size: number }[];
}

/**
 * Package OPFS files and dbKey into a single Uint8Array.
 * Format: [4-byte header length][JSON header][file1 bytes][file2 bytes]...
 */
function packBackup(files: Map<string, Uint8Array>, dbKey: Uint8Array): Uint8Array {
  const fileEntries: { name: string; size: number }[] = [];
  const fileBuffers: Uint8Array[] = [];

  files.forEach((bytes, name) => {
    fileEntries.push({ name, size: bytes.length });
    fileBuffers.push(bytes);
  });

  const header: BackupHeader = {
    version: BACKUP_VERSION,
    dbKey: Array.from(dbKey),
    files: fileEntries,
  };

  const headerJson = new TextEncoder().encode(JSON.stringify(header));
  const headerLen = new Uint32Array([headerJson.length]);
  const headerLenBytes = new Uint8Array(headerLen.buffer);

  // Total size: 4 (header length) + header + all file bytes
  let totalSize = 4 + headerJson.length;
  for (const buf of fileBuffers) totalSize += buf.length;

  const result = new Uint8Array(totalSize);
  let offset = 0;

  result.set(headerLenBytes, offset); offset += 4;
  result.set(headerJson, offset); offset += headerJson.length;
  for (const buf of fileBuffers) {
    result.set(buf, offset);
    offset += buf.length;
  }

  return result;
}

/**
 * Unpack a backup blob into OPFS files and dbKey.
 */
function unpackBackup(data: Uint8Array): { files: Map<string, Uint8Array>; dbKey: Uint8Array } {
  const headerLenBuf = new Uint32Array(data.buffer.slice(data.byteOffset, data.byteOffset + 4));
  const headerLen = headerLenBuf[0];

  const headerJson = new TextDecoder().decode(data.slice(4, 4 + headerLen));
  const header: BackupHeader = JSON.parse(headerJson);

  if (header.version !== BACKUP_VERSION) {
    throw new Error(`Unsupported backup version: ${header.version}`);
  }

  const dbKey = new Uint8Array(header.dbKey);
  const files = new Map<string, Uint8Array>();

  let offset = 4 + headerLen;
  for (const entry of header.files) {
    files.set(entry.name, data.slice(offset, offset + entry.size));
    offset += entry.size;
  }

  return { files, dbKey };
}

// ---- Storage path helper ----

/**
 * Get the Firebase Storage path for backups.
 * Uses Firebase auth UID so that Storage rules can enforce:
 *   request.auth.uid == {uid} in the path
 *
 * - Custodial users: UID = Firebase user ID (from social login)
 * - Non-custodial users: UID = wallet address (from custom token)
 *
 * Must be called after ensureFirebaseAuth().
 */
function getBackupPath(fallbackAddress: string): string {
  const uid = getFirebaseUid();
  const pathId = uid || fallbackAddress.toLowerCase();
  return `xmtp-backups/${pathId}/backup.enc`;
}

// ---- Public API ----

// Debounce flag to prevent concurrent backups
let backupInProgress = false;

/**
 * Back up the XMTP OPFS database to Firebase Storage.
 * Encrypted with a wallet-derived AES-GCM key for portability across devices.
 */
export async function backupXmtpDatabase(
  address: string,
  signer: HorusSigner,
  walletAddress?: string,
): Promise<void> {
  if (backupInProgress) {
    console.log('[XMTP Backup] ⏭ Backup already in progress, skipping');
    return;
  }
  backupInProgress = true;

  try {
    const addr = address.toLowerCase();
    console.log(`[XMTP Backup] ▶ Starting backup for ${addr}`);

    // 1. Export OPFS files
    console.log('[XMTP Backup] Step 1/7: Exporting OPFS files...');
    const opfsFiles = await exportOpfsFiles(addr);
    if (opfsFiles.size === 0) {
      console.error('[XMTP Backup] ✗ Step 1 FAILED: No OPFS files found — nothing to backup');
      return;
    }
    console.log(`[XMTP Backup] ✓ Step 1: Found ${opfsFiles.size} OPFS files`);

    // 2. Get dbEncryptionKey from localStorage
    console.log('[XMTP Backup] Step 2/7: Reading dbEncryptionKey from localStorage...');
    const dbKeyRaw = localStorage.getItem(DB_KEY_PREFIX + addr);
    if (!dbKeyRaw) {
      console.error('[XMTP Backup] ✗ Step 2 FAILED: No dbEncryptionKey in localStorage at key:', DB_KEY_PREFIX + addr);
      return;
    }

    let dbKey: Uint8Array;
    try {
      const arr = JSON.parse(dbKeyRaw);
      dbKey = new Uint8Array(arr);
    } catch {
      console.error('[XMTP Backup] ✗ Step 2 FAILED: dbEncryptionKey in localStorage is corrupted');
      return;
    }
    console.log(`[XMTP Backup] ✓ Step 2: dbEncryptionKey loaded (${dbKey.length} bytes)`);

    // 3. Package files + dbKey
    console.log('[XMTP Backup] Step 3/7: Packaging files...');
    const packed = packBackup(opfsFiles, dbKey);
    console.log(`[XMTP Backup] ✓ Step 3: Packed ${opfsFiles.size} files (${(packed.length / 1024).toFixed(1)} KB)`);

    // 4. Derive encryption key from wallet signature
    console.log('[XMTP Backup] Step 4/7: Deriving backup encryption key...');
    const backupKey = await deriveBackupKey(signer);
    console.log(`[XMTP Backup] ✓ Step 4: Backup key derived (${backupKey.length} bytes)`);

    // 5. Encrypt
    console.log('[XMTP Backup] Step 5/7: Encrypting...');
    const { payload, nonce } = await encryptBlob(packed, backupKey);
    console.log(`[XMTP Backup] ✓ Step 5: Encrypted (${(payload.length / 1024).toFixed(1)} KB)`);

    // 6. Prepend nonce to payload (12 bytes nonce + encrypted data)
    const uploadData = new Uint8Array(12 + payload.length);
    uploadData.set(nonce, 0);
    uploadData.set(payload, 12);
    console.log(`[XMTP Backup] ✓ Step 6: Upload data prepared (${(uploadData.length / 1024).toFixed(1)} KB)`);

    // 7. Upload to Firebase Storage (path uses Firebase UID for rule enforcement)
    console.log('[XMTP Backup] Step 7/7: Authenticating with Firebase...');
    await ensureFirebaseAuth(walletAddress || addr);
    const uid = getFirebaseUid();
    console.log(`[XMTP Backup] Firebase UID: ${uid || 'NULL'}`);
    const path = getBackupPath(addr);
    console.log(`[XMTP Backup] Uploading to path: ${path} (${(uploadData.length / 1024).toFixed(1)} KB)...`);
    const downloadUrl = await uploadEncryptedFile(path, uploadData);

    console.log(`[XMTP Backup] ✓✓✓ Backup complete! URL: ${downloadUrl}`);
  } catch (err: any) {
    console.error('[XMTP Backup] ✗✗✗ Backup FAILED:', err?.message || err);
    console.error('[XMTP Backup] Full error:', err);
  } finally {
    backupInProgress = false;
  }
}

/**
 * Check if a Firebase backup already exists for this user.
 * Must be called after ensureFirebaseAuth().
 */
export async function hasFirebaseBackup(fallbackAddress: string): Promise<boolean> {
  try {
    const storage = getStorage(getApp());
    const path = getBackupPath(fallbackAddress);
    console.log(`[XMTP Backup] hasFirebaseBackup: checking path ${path}`);
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    console.log(`[XMTP Backup] hasFirebaseBackup: found backup at ${url.slice(0, 80)}...`);
    return true;
  } catch (err: any) {
    console.log(`[XMTP Backup] hasFirebaseBackup: no backup found (${err?.code || err?.message || 'unknown error'})`);
    return false;
  }
}

/**
 * Sync local XMTP data to Firebase Storage.
 * Always uploads the current local state to keep Firebase as the source of truth.
 * Called after successful local builds and periodic backups.
 */
export async function ensureFirebaseBackup(
  address: string,
  signer: HorusSigner,
  walletAddress?: string,
): Promise<void> {
  const addr = address.toLowerCase();
  try {
    console.log('[XMTP Backup] ensureFirebaseBackup: starting for', addr);
    await ensureFirebaseAuth(walletAddress || addr);
    const uid = getFirebaseUid();
    if (!uid) {
      console.error('[XMTP Backup] ensureFirebaseBackup: Firebase auth failed — no UID. Cannot backup.');
      return;
    }
    console.log('[XMTP Backup] ensureFirebaseBackup: uploading local state to Firebase...');
    await backupXmtpDatabase(address, signer, walletAddress);
    console.log('[XMTP Backup] ensureFirebaseBackup: done');
  } catch (err: any) {
    console.error('[XMTP Backup] ensureFirebaseBackup FAILED:', err?.message || err);
  }
}

/**
 * Restore the XMTP OPFS database from a Firebase Storage backup.
 * Returns true if restoration was successful, false if no backup exists.
 */
export async function restoreXmtpDatabase(
  address: string,
  signer: HorusSigner,
  walletAddress?: string,
): Promise<boolean> {
  try {
    const addr = address.toLowerCase();

    // 1. Get the backup download URL from Firebase Storage (path uses Firebase UID)
    await ensureFirebaseAuth(walletAddress || addr);

    let downloadUrl: string;
    try {
      const storage = getStorage(getApp());
      const storageRef = ref(storage, getBackupPath(addr));
      downloadUrl = await getDownloadURL(storageRef);
    } catch {
      // No backup exists
      return false;
    }

    // 2. Download encrypted backup
    const uploadData = await downloadEncryptedFile(downloadUrl);
    if (uploadData.length < 12) return false;

    // 3. Extract nonce (first 12 bytes) and encrypted payload
    const nonce = uploadData.slice(0, 12);
    const payload = uploadData.slice(12);

    // 4. Derive decryption key from wallet signature
    const backupKey = await deriveBackupKey(signer);

    // 5. Decrypt
    const packed = await decryptBlob(payload, backupKey, nonce);

    // 6. Unpack files + dbKey
    const { files, dbKey } = unpackBackup(packed);

    // 7. Write OPFS files
    await importOpfsFiles(files);

    // 8. Restore dbEncryptionKey to localStorage
    localStorage.setItem(DB_KEY_PREFIX + addr, JSON.stringify(Array.from(dbKey)));

    console.log(`XMTP backup restored (${files.size} files, ${(packed.length / 1024).toFixed(1)} KB)`);
    return true;
  } catch (err) {
    console.warn('XMTP backup restore failed:', err);
    return false;
  }
}
